type DebounceSettings = {
	readonly leading?: boolean;
	readonly trailing?: boolean;
	readonly maxWait?: number;
};

type DebouncedFunc<T extends (...args: any[]) => any> = {
	(...args: Parameters<T>): ReturnType<T> | undefined;
	cancel(): void;
	flush(): ReturnType<T> | undefined;
};

function debounce<T extends (...args: any) => any>(
	func: T,
	wait: number,
	settings?: DebounceSettings,
): DebouncedFunc<T> {
	let lastArgs: unknown[] | undefined;
	let lastThis: unknown;
	let result: ReturnType<T> | undefined;
	let timerId: ReturnType<typeof setTimeout> | undefined;

	let lastCallTime: number | undefined;
	let maxWait: number | undefined;
	let lastInvokeTime = 0;
	let leading = false;
	let trailing = true;

	if (settings !== undefined) {
		leading = !!settings.leading;
		maxWait =
			"maxWait" in settings ? Math.max(settings.maxWait ?? 0, wait) : undefined;
		trailing = "trailing" in settings ? !!settings.trailing : trailing;
	}

	function invokeFunc(time: number) {
		if (lastArgs === undefined) {
			throw new Error("Invalid state, func not called");
		}
		const args = lastArgs;
		const thisArg = lastThis;

		lastArgs = lastThis = undefined;
		lastInvokeTime = time;
		result = func.apply(thisArg, args);
		return result;
	}

	function leadingEdge(time: number) {
		// Reset any `maxWait` timer.
		lastInvokeTime = time;
		// Start the timer for the trailing edge.
		timerId = setTimeout(timerExpired, wait);
		// Invoke the leading edge.
		return leading ? invokeFunc(time) : result;
	}

	function remainingWait(time: number) {
		const timeSinceLastCall = time - (lastCallTime ?? 0);
		const timeSinceLastInvoke = time - lastInvokeTime;
		const timeWaiting = wait - timeSinceLastCall;

		return maxWait !== undefined
			? Math.min(timeWaiting, maxWait - timeSinceLastInvoke)
			: timeWaiting;
	}

	function shouldInvoke(time: number) {
		const timeSinceLastCall = time - (lastCallTime ?? 0);
		const timeSinceLastInvoke = time - lastInvokeTime;

		// Either this is the first call, activity has stopped and we're at the
		// trailing edge, the system time has gone backwards and we're treating
		// it as the trailing edge, or we've hit the `maxWait` limit.
		return (
			lastCallTime === undefined ||
			timeSinceLastCall >= wait ||
			timeSinceLastCall < 0 ||
			(maxWait !== undefined && timeSinceLastInvoke >= maxWait)
		);
	}

	function timerExpired() {
		const time = Date.now();
		if (shouldInvoke(time)) {
			return trailingEdge(time);
		}
		// Restart the timer.
		timerId = setTimeout(timerExpired, remainingWait(time));
		return undefined;
	}

	function trailingEdge(time: number) {
		timerId = undefined;

		// Only invoke if we have `lastArgs` which means `func` has been
		// debounced at least once.
		if (trailing && lastArgs) {
			return invokeFunc(time);
		}
		lastArgs = lastThis = undefined;
		return result;
	}

	function cancel() {
		if (timerId !== undefined) {
			clearTimeout(timerId);
		}
		lastInvokeTime = 0;
		lastArgs = lastCallTime = lastThis = timerId = undefined;
	}

	function flush() {
		return timerId === undefined ? result : trailingEdge(Date.now());
	}

	function debounced(...args: unknown[]) {
		const time = Date.now();
		const isInvoking = shouldInvoke(time);

		lastArgs = args;
		// @ts-expect-error I don't know a way to type "this"
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		lastThis = this;
		lastCallTime = time;

		if (isInvoking) {
			if (timerId === undefined) {
				return leadingEdge(lastCallTime);
			}
			if (maxWait !== undefined) {
				// Handle invocations in a tight loop.
				clearTimeout(timerId);
				timerId = setTimeout(timerExpired, wait);
				return invokeFunc(lastCallTime);
			}
		}
		if (timerId === undefined) {
			timerId = setTimeout(timerExpired, wait);
		}
		return result;
	}
	debounced.cancel = cancel;
	debounced.flush = flush;
	return debounced;
}

// type Function0<R> = () => R;
// type Function1<T1, R> = (t1: T1) => R;
// type Function2<T1, T2, R> = (t1: T1, t2: T2) => R;
// type Function3<T1, T2, T3, R> = (t1: T1, t2: T2, t3: T3) => R;
// type Function4<T1, T2, T3, T4, R> = (t1: T1, t2: T2, t3: T3, t4: T4) => R;
type Partial = {
	// <T1, T2, R>(func: Function2<T1, T2, R>, plc1: __, arg2: T2): Function1<T1, R>;
	// <T1, T2, T3, R>(func: Function3<T1, T2, T3, R>, plc1: __, arg2: T2): Function2<T1, T3, R>;
	// <T1, T2, T3, R>(func: Function3<T1, T2, T3, R>, plc1: __, plc2: __, arg3: T3): Function2<T1, T2, R>;
	// <T1, T2, T3, R>(func: Function3<T1, T2, T3, R>, arg1: T1, plc2: __, arg3: T3): Function1<T2, R>;
	// <T1, T2, T3, R>(func: Function3<T1, T2, T3, R>, plc1: __, arg2: T2, arg3: T3): Function1<T1, R>;
	// <T1, T2, T3, T4, R>(func: Function4<T1, T2, T3, T4, R>, plc1: __, arg2: T2): Function3<T1, T3, T4, R>;
	// <T1, T2, T3, T4, R>(func: Function4<T1, T2, T3, T4, R>, plc1: __, plc2: __, arg3: T3): Function3<T1, T2, T4, R>;
	// <T1, T2, T3, T4, R>(func: Function4<T1, T2, T3, T4, R>, arg1: T1, plc2: __, arg3: T3): Function2<T2, T4, R>;
	// <T1, T2, T3, T4, R>(func: Function4<T1, T2, T3, T4, R>, plc1: __, arg2: T2, arg3: T3): Function2<T1, T4, R>;
	// <T1, T2, T3, T4, R>(func: Function4<T1, T2, T3, T4, R>, plc1: __, plc2: __, plc3: __, arg4: T4): Function3<T1, T2, T3, R>;
	// <T1, T2, T3, T4, R>(func: Function4<T1, T2, T3, T4, R>, arg1: T1, plc2: __, plc3: __, arg4: T4): Function2<T2, T3, R>;
	// <T1, T2, T3, T4, R>(func: Function4<T1, T2, T3, T4, R>, plc1: __, arg2: T2, plc3: __, arg4: T4): Function2<T1, T3, R>;
	// <T1, T2, T3, T4, R>(func: Function4<T1, T2, T3, T4, R>, arg1: T1, arg2: T2, plc3: __, arg4: T4): Function1<T3, R>;
	// <T1, T2, T3, T4, R>(func: Function4<T1, T2, T3, T4, R>, plc1: __, plc2: __, arg3: T3, arg4: T4): Function2<T1, T2, R>;
	// <T1, T2, T3, T4, R>(func: Function4<T1, T2, T3, T4, R>, arg1: T1, plc2: __, arg3: T3, arg4: T4): Function1<T2, R>;
	// <T1, T2, T3, T4, R>(func: Function4<T1, T2, T3, T4, R>, plc1: __, arg2: T2, arg3: T3, arg4: T4): Function1<T1, R>;
	<TS extends any[], R>(func: (...ts: TS) => R): (...ts: TS) => R;
	<TS extends any[], T1, R>(
		func: (t1: T1, ...ts: TS) => R,
		arg1: T1,
	): (...ts: TS) => R;
	<TS extends any[], T1, T2, R>(
		func: (t1: T1, t2: T2, ...ts: TS) => R,
		t1: T1,
		t2: T2,
	): (...ts: TS) => R;
	<TS extends any[], T1, T2, T3, R>(
		func: (t1: T1, t2: T2, t3: T3, ...ts: TS) => R,
		t1: T1,
		t2: T2,
		t3: T3,
	): (...ts: TS) => R;
	<TS extends any[], T1, T2, T3, T4, R>(
		func: (t1: T1, t2: T2, t3: T3, t4: T4, ...ts: TS) => R,
		t1: T1,
		t2: T2,
		t3: T3,
		t4: T4,
	): (...ts: TS) => R;
	// placeholder: __;
};

function basePartial(
	// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
	func: Function,
	...args: Array<any>
) {
	return function (...callArgs: Array<unknown>) {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument
		return func(...[...args, ...callArgs]);
	};
}

const partial: Partial = basePartial;

export { debounce, partial };
